import React, { createContext, useContext, useEffect, useState,useRef } from 'react';
import { supabase } from '../lib/supabase';
import { fetchEffectivePermissionCodes } from '../lib/effectivePermissions';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
 const [userProfile, setUserProfile] = useState(() => {
  // Initialize from localStorage immediately
  const stored = localStorage.getItem('authUser');
  return stored ? JSON.parse(stored) : undefined;
});
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const loadedUserIdRef = useRef(null);
  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
     if (!userId || loadedUserIdRef.current === userId) return; // Skip if same user
      loadedUserIdRef.current = userId;
      setProfileLoading(true)
      try {
       
        const { data: profileData, error: profileError  } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.maybeSingle()
        
     const { data: roleData, error: roleError } = await supabase
  .from('user_role_assignments')
  .select(`
    *,
    user_roles (
      id,
      role_name,
      role_type,
      can_be_client,
      can_be_supplier
    )
  `)
  .eq('user_id', userId)
  .eq('is_active', true);
 

     //   if (!profileError && !roleError) setUserProfile({...profileData, role: roleData?.user_roles.role_type || null })
      

    const roles = roleData?.map(assignment => assignment.user_roles).filter(Boolean) || [];
    const roleTypes = roles.map(r => r.role_type).filter(Boolean);
    const primaryRole = roleTypes[0] || null;

    let permissionCodes = [];
    try {
      permissionCodes = await fetchEffectivePermissionCodes(supabase, userId);
    } catch (e) {
      console.error('Failed to fetch permission codes:', e);
    }

    const nextProfile = {
      ...(profileError ? {} : profileData),
      roles,
      roleTypes,
      primaryRole,
      permissionCodes,
    };

    if (roleTypes.length || !profileError) {
      setUserProfile((prev) => ({
        ...(prev || {}),
        ...nextProfile,
        ...(profileData && !profileError ? profileData : {}),
      }));
      try {
        const storedRaw = localStorage.getItem('authUser');
        if (storedRaw) {
          const u = JSON.parse(storedRaw);
          if (u?.id === userId) {
            localStorage.setItem(
              'authUser',
              JSON.stringify({
                ...u,
                roleTypes: nextProfile.roleTypes,
                roles,
                primaryRole: nextProfile.primaryRole,
                permissionCodes,
              }),
            );
          }
        }
      } catch (_) {
        /* ignore */
      }
    } else if (roleError) {
      console.error('Failed to fetch roles:', roleError);
    }
    } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },
   /* async load(userId) {
  if (!userId) return
  setProfileLoading(true)
  try {
    // Get auth user data
    const { data: { user }, error } = await supabase.auth.getUser();
    console.log('AuthContext userProfile:', user);
    if (!error && user) {
      setUserProfile({
        id: user.id,
        email: user.email,
        ...user.user_metadata,
        created_at: user.created_at,
        phone: user.phone,
        role: user.raw_user_meta_data?.role || null,
      });
    }
  } catch (error) {
    console.error('Profile load error:', error)
  } finally {
    setProfileLoading(false)
  }
},
*/
    clear() {
      loadedUserIdRef.current = null;
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
const signIn = async (email, password) => {
  try {
    const { data, error } = await supabase?.auth?.signInWithPassword({ email, password });
    
    if (error || !data?.user) {
      return { data, error };
    }
    console.log('Sign-in successful, fetching roles for user:', data.user);

    const { data: profileRow, error: profileError } = await supabase
      .from('user_profiles')
      .select('is_active')
      .eq('id', data.user.id)
      .maybeSingle();

    if (!profileError && profileRow && profileRow.is_active === false) {
      await supabase.auth.signOut();
      return {
        data: null,
        error: {
          message:
            'Votre compte est en attente d\'activation par un administrateur.',
        },
      };
    }

    // ── Fetch all roles ─────────────────────────────────────────────────────
    const { data: roleData, error: roleError } = await supabase
      .from('user_role_assignments')
      .select(`
        *,
        user_roles (
          id,
          role_name,
          role_type,
          can_be_client,
          can_be_supplier
        )
      `)
      .eq('user_id', data.user.id)
      .eq('is_active', true);

    if (roleError) {
      console.error('Failed to fetch roles:', roleError);
    }

    // ── Transform and store ─────────────────────────────────────────────────
    const roles = roleData?.map(assignment => assignment.user_roles) || [];
    const roleTypes = roles.map(r => r.role_type);
    const primaryRole = roles[0]?.role_type || null;

    let permissionCodes = [];
    try {
      permissionCodes = await fetchEffectivePermissionCodes(supabase, data.user.id);
    } catch (e) {
      console.error('Failed to fetch permission codes on sign-in:', e);
    }

    // Build enhanced user object
    const authUser = {
      ...data.user,
      roles,
      roleTypes,
      primaryRole,
      permissionCodes,
    };

    localStorage.setItem('authUser', JSON.stringify(authUser));
    loadedUserIdRef.current = null;
    setUserProfile({
      id: data.user.id,
      email: data.user.email,
      roles,
      roleTypes,
      primaryRole,
      permissionCodes,
    });

    return { data: { ...data, user: authUser }, error: null };
    
  } catch (error) {
    return { error: { message: 'Network error. Please try again.' } };
  }
};

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
         localStorage.removeItem('authUser');
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }




  const value = {
    user,
    userProfile,
    permissionCodes: userProfile?.permissionCodes ?? [],
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
